import { CommentDto } from './comment.dto';
import type { InteractionBaseDto } from './interactions/interaction-base.dto';
import { UpsertUserNeedRequestSchema } from './needs.dto';
import {
  NeedResourceInteractionDto,
  ResourceInteractionDto,
  ResourcingRequest,
  ResourcingRequestSchema,
} from './resourcing.dto';
import type { UserDto } from './user.dto';
import { CallStatus } from '../../../definitions';
import { CallConnectionLogEvent } from '../../../types';
import { VoiceCallRecordingDto } from './voice-call-recording.dto';
import { VoiceCallAuditDto } from './voice-call-audit.dto';
import z from 'zod';

export interface PaginatedCallsDto {
  calls: CallDto[];
  count: number;
  users: UserDto[];
}

export type ConnectionLogsDto = {
  id: number;
  voiceCallConnectionId: number;
  timestamp: number;
  event: CallConnectionLogEvent;
  status?: string;
  actingUser?: UserDto;
  createdAt: number;
};

export type CallDto = {
  id: number;
  sid: string;
  incoming: boolean;
  subjectId: string;
  wasOriginallyUnknown: boolean;
  status: CallStatus;
  systemStatus: string;
  timeStarted: number;
  timeConnected: number;
  timeEnded: number;
  voicemailFileId: string;
  voicemailTranscription: string;
  note: string;
  outcomeId: number;
  noFollowUpNeeded: boolean;
  comments: CommentDto[];
  resourceInteraction: ResourceInteractionDto;
  needResourceInteraction: NeedResourceInteractionDto;
  originator: CallUser;
  receiver: CallUser;
  insufficientNotes: boolean;
  isFeedbackCall: boolean;
  originatorConnectionLogs: ConnectionLogsDto[];
  receiverConnectionLogs: ConnectionLogsDto[];
  connections?: CallConnectionDto[];
  recording: VoiceCallRecordingDto;
  voiceCallAudits: VoiceCallAuditDto[];
  auditorId?: string;
  isLlmSuggestionsProcessed: boolean;
} & InteractionBaseDto<'call'>;

export interface CallDataSource extends CallDto {
  length: number;
  from: string;
  to: string;
}

export interface CallUser {
  userId: string;
  phoneNumber: string;
  errorCode: number;
  onHold?: boolean;
}

export interface CallConnectionDto {
  id: number;
  callId: number;
  userId: string;
  sid: string;
  phoneNumber: string;
  status: string;
  onHold: boolean;
  isAdded: boolean;
  connectedAt: number;
  completedAt: number;
  connectionLogs?: ConnectionLogsDto[];
  config?: {
    title?: string;
    [key: string]: unknown;
  };
}

export const AddGuideToCallRequestSchema = z.object({
  guideId: z.string().nonempty('Guide ID is required'),
  memberId: z.string().optional(),
  title: z.string().optional(),
  isColdTransfer: z.boolean().optional(),
});

export interface ToggleConnectionHoldRequestDto {
  connectionId: number;
  onHold: boolean;
}

export interface HangupConnectionRequestDto {
  connectionId: number;
}

export interface CallCreationRequest {
  targetUserId?: string;
  phoneNumberId?: number;
  phoneNumber?: string;
  scheduledEventId?: number;
  userNeedId?: number;
  resourceInteraction?: ResourcingRequest;
  needResourceInteraction?: ResourcingRequest;
  isFeedbackCall?: boolean;
}

export const CallCreationRequestSchema = z.object({
  targetUserId: z.string().optional(),
  phoneNumberId: z.number().optional(),
  phoneNumber: z.string().optional(),
  scheduledEventId: z.number().optional().nullable(),
  userNeedId: z.number().optional().nullable(),
  resourceInteraction: ResourcingRequestSchema.optional().nullable(),
  needResourceInteraction: ResourcingRequestSchema.optional().nullable(),
});

export type CallCreationRequestForm = z.infer<typeof CallCreationRequestSchema>;
export interface ReturnCallCreationRequestDto {
  callId: number;
}

export interface CallOutcomeDto {
  id: number;
  key: string;
  text: string;
  outcomeTypes: string[];
  deprecatedAt: number;
}

export const AssignPhoneCallSchema = z.object({
  interactableId: z.string().nonempty('Required'),
});

export type AssignPhoneCallForm = z.infer<typeof AssignPhoneCallSchema>;

export const UpdateCallSchema = z.object({
  outcomeId: z.number().optional().nullable(),
  note: z.string().optional(),
  resourceTitle: z.string().optional(),
  resourceInteraction: ResourcingRequestSchema.optional(),
  needResourceInteraction: ResourcingRequestSchema.optional(),
  userNeedRequest: UpsertUserNeedRequestSchema.optional(),
  callAuditorId: z.string().optional(),
});

export type UpdateCallForm = z.infer<typeof UpdateCallSchema>;

export interface PhoneCallForm {
  outcomeId: number;
  note: string;
  userNeedId: number;
  needId: number;
  needCategory: number;
  needStatus: string;
  needTitle: string;
  resourceName: string;
  resourceNumber: string;
  selectedPhoneNumberId: number;
}

export interface ValidateActiveCallsResponseDto {
  activeCallIds: number[];
  endedCallIds: number[];
}
